// Form atoms — TODO T18 (31 Aug 2026), restyled to v2 at T30 (1 Sep 2026).
//
// Same components, new skin: the controls below are the ones T18 made real, redrawn against v2's
// tokens (9px radii, the champagne focus ring, uppercase micro labels, the pill `Button` which
// now lives in `ui/atoms.tsx` with the rest of the atoms).
//
// Three rules the screens rely on, unchanged since T18:
//
//  1. A FIELD OWNS ITS LABEL. `<Field label>` renders a real `<label>` bound to the control, so a
//     form is usable by keyboard and by a screen reader without every screen remembering to.
//  2. `hint` IS FOR RULES THAT ARE NOT OBVIOUS. The client contact's number is the reason this
//     prop exists — "the personal number they would actually text from" is a field-level
//     instruction, not a convention (docs/decisions.md, 24 Aug 2026).
//  3. A PENDING WRITE DISABLES ITS BUTTON. Everything here is a real database write on a service
//     one recruiter shares with others; a double-submitted "Generate code" is two codes, and the
//     second one is confusing forever.
import { withFirmName } from './atoms.tsx';
import type { ChangeEvent, FormEvent, ReactNode } from 'react';
import { ApiError } from '../api/core.ts';

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="field">
      <span className="label">{withFirmName(label)}</span>
      {children}
      {hint && <span className="field__hint">{hint}</span>}
    </label>
  );
}

interface TextProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  /** `password` since T70: a Fathom key pasted into Settings is never echoed on screen. */
  type?: 'text' | 'email' | 'tel' | 'date' | 'datetime-local' | 'password';
  maxLength?: number;
  /** For a box with no label of its own beside it — the roles picker's find (T38b). */
  'aria-label'?: string;
}

export function TextInput({ value, onChange, type = 'text', ...rest }: TextProps) {
  return (
    <input
      className="input"
      type={type}
      value={value}
      onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
      {...rest}
    />
  );
}

export function TextArea({ value, onChange, rows = 3, ...rest }: TextProps & { rows?: number }) {
  return (
    <textarea
      className="input input--area"
      rows={rows}
      value={value}
      onChange={(event: ChangeEvent<HTMLTextAreaElement>) => onChange(event.target.value)}
      {...rest}
    />
  );
}

export function Select<T extends string>({
  value,
  onChange,
  options,
  groups = [],
  disabled,
  compact = false,
}: {
  value: T;
  onChange: (value: T) => void;
  options: readonly { value: T; label: string }[];
  /** Further options under a heading each (an <optgroup>), after the plain ones. */
  groups?: readonly { label: string; options: readonly { value: T; label: string }[] }[];
  disabled?: boolean;
  /** Inline in a table cell rather than filling a form row. */
  compact?: boolean;
}) {
  return (
    <select
      className={compact ? 'input input--select input--compact' : 'input input--select'}
      value={value}
      disabled={disabled}
      onChange={(event: ChangeEvent<HTMLSelectElement>) => onChange(event.target.value as T)}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
      {groups.map((group) => (
        <optgroup key={group.label} label={group.label}>
          {group.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

export function Checkbox({
  checked,
  onChange,
  label,
  hint,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  hint?: string;
  disabled?: boolean;
}) {
  return (
    <label className="checkbox">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.checked)}
      />
      <span>
        <span className="checkbox__label">{label}</span>
        {hint && <span className="field__hint">{hint}</span>}
      </span>
    </label>
  );
}

/**
 * A two-or-three way switch, v2's pill. Used for the theme in Settings; a radio group underneath,
 * so the choice is one tab stop and the arrow keys work.
 */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  label,
}: {
  value: T;
  onChange: (value: T) => void;
  options: readonly { value: T; label: string }[];
  label: string;
}) {
  return (
    <div className="segmented" role="radiogroup" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          className={option.value === value ? 'on' : undefined}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** A filter pill — the chips on the searches home and the conversations list (T36). */
export function Chip({
  on,
  onClick,
  children,
}: {
  on: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button type="button" className={on ? 'chip chip--on' : 'chip'} onClick={onClick}>
      {children}
    </button>
  );
}

export function Form({ onSubmit, children }: { onSubmit: () => void; children: ReactNode }) {
  return (
    <form
      className="form"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      {children}
    </form>
  );
}

export function Actions({ children }: { children: ReactNode }) {
  return <div className="form__actions">{children}</div>;
}

/**
 * What a refused write looks like to a recruiter.
 *
 * The service answers `{ "error": "<code>" }` and nothing else — the detail is a log line there,
 * deliberately (services/bff/src/auth.ts) — so this is where a code becomes a sentence.
 * `conflict` is the one worth reading: it is always a real condition somebody can act on, never
 * a bug. A code with no sentence is shown as itself rather than swallowed.
 *
 * THE ONE FORM THE SERVICE ANSWERS IN FULL (8 Sep 2026): a candidate entry refused by the parser
 * comes back with its messages, one per field, and they are shown verbatim in place of the
 * sentence — "entry.e164: "+972 587290989" is not E.164 (…)" names the field and the rule, where
 * "check the required fields" named neither and sent a recruiter looking for a blank that was
 * not there. The same words the worker's refusal uses on that form (screens/CandidateEntryScreen).
 */
const WRITE_EXPLANATIONS: Record<string, string> = {
  bad_request: 'Something on the form was not accepted. Check the required fields.',
  conflict: 'That cannot be done as things stand. See the note beside the control.',
  // T330c: BEING_DELETED_SENTENCE in the service, the same words.
  being_deleted: 'Someone linked to this is being deleted. Try again in a few minutes.',
  not_found: 'That record is gone. Reload the screen.',
  email_unavailable: 'The code was not emailed. Nothing was lost: try sending it again.',
  // T53a. No contact provider's key on this server.
  contacts_unavailable: 'Looking up a number is not set up on this server.',
  // T107a. No media bucket on this server, so no file or voice note can be downloaded.
  file_unavailable: 'Downloading files is not set up on this server.',
  unauthenticated: 'The session expired. Sign in again.',
  not_a_recruiter: 'This account is no longer linked to a staff record.',
  // T42, T554. The put-forward's one named refusal: they said no, recorded on their page.
  consent_refused: 'They said no to being put forward. Their page records it.',
  network: 'The brain API did not answer. Is it running?',
  internal: 'The brain API failed. The detail is in its log, not here.',
};

export function WriteError({ error, what }: { error: unknown; what: string }) {
  if (!error) return null;
  const code = error instanceof ApiError ? error.code : null;
  const problems = error instanceof ApiError ? error.problems : [];
  return (
    <div className="form__error">
      Could not {what}.
      {problems.length > 0 ? (
        <ul className="form__problems">
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      ) : (
        code && <> {WRITE_EXPLANATIONS[code] ?? code}</>
      )}
    </div>
  );
}
